import AutoLoadingButton from "@/components/AutoLoadingButton";
import LoadingDots from "@/components/LoadingDots";
import ShopDTO from "@/dto/shop/ShopDTO";
import { InjectBaseModalProps } from "@/hooks/Modal/ModalStore";
import LogisticsCompanyNameMap from "@/map/LogisticsCompanyNameMap";
import {
    getInstantShipOrderRecords,
    InstantShipOrderRecord,
    InstantShipOrderRecordType,
    InstantShipTask,
    InstantShipTaskStatus,
} from "@/services/api/instantShip";
import { confirm } from "@/utils";
import { Alert, Badge, Modal, Table, Tag } from "antd";
import type { TableColumnsType, TablePaginationConfig } from "antd";
import moment from "moment";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useRef, useState } from "react";
import InstantShipStore from "../../store/InstantShipStore";
import { instantShipOrderRecordTypeNameMap, instantShipPostPayIntervalNameMap } from "./constants";

interface TaskModalProps extends InjectBaseModalProps {
    shop: ShopDTO;
    instantShipStore: InstantShipStore;
    taskId: number;
}

const PAGE_SIZE = 20;
const ALL_RECORD_FILTER = "ALL" as const;

type InstantShipRecordFilter =
    | typeof ALL_RECORD_FILTER
    | InstantShipOrderRecordType.SUCCESS
    | InstantShipOrderRecordType.GENERATE_FAIL
    | InstantShipOrderRecordType.SHIP_FAIL
    | InstantShipOrderRecordType.SKIP;

function formatTime(value?: string) {
    if (!value) return "-";
    return moment(value).format("YYYY-MM-DD HH:mm:ss");
}

function renderGenerateResult(record: InstantShipOrderRecord) {
    if (record.type === InstantShipOrderRecordType.GENERATE_FAIL) {
        return <span className="instant-ship-result-error">{record.message || "取号失败"}</span>;
    }

    if (record.type === InstantShipOrderRecordType.SKIP) {
        return <span className="text-gray">{record.message || "订单已跳过"}</span>;
    }

    return (
        <div>
            <div>
                <span>{LogisticsCompanyNameMap[record.logisticsCompany]}</span>
                <span className="ml5">{record.waybillNos || "-"}</span>
            </div>
            <div className="text-gray text-small">{formatTime(record.generateTime)}</div>
        </div>
    );
}

function renderShippingResult(record: InstantShipOrderRecord) {
    if (record.type === InstantShipOrderRecordType.SUCCESS) {
        return (
            <div>
                <span className="instant-ship-result-success">发货成功</span>
                <div className="text-gray text-small">{formatTime(record.shippingTime)}</div>
            </div>
        );
    }

    if (record.type === InstantShipOrderRecordType.SHIP_FAIL) {
        return <span className="instant-ship-result-error">{record.message || "发货失败"}</span>;
    }

    return "-";
}

const columns: TableColumnsType<InstantShipOrderRecord> = [
    {
        title: "序号",
        width: 70,
        align: "center",
        render: (_, __, index) => index + 1,
    },
    {
        title: "订单号",
        dataIndex: "originalId",
        width: 175,
        render: (value, record) => <div>{record.splitChildOrderOriginalId || value || "-"}</div>,
    },
    {
        title: "收货人",
        dataIndex: "consignee",
        width: 300,
        render: (value) => value || "-",
    },
    {
        title: "付款时间",
        dataIndex: "paymentTime",
        width: 100,
        render: (value) => (
            <div>
                <div>{moment(value).format("YYYY-MM-DD")}</div>
                <div>{moment(value).format("HH:mm:ss")}</div>
            </div>
        ),
    },
    {
        title: "取号结果",
        width: 230,
        render: (_, record) => renderGenerateResult(record),
    },
    {
        title: "发货结果",
        width: 220,
        render: (_, record) => renderShippingResult(record),
    },
    {
        title: "处理状态",
        dataIndex: "type",
        width: 120,
        render: (value: InstantShipOrderRecordType) => (
            <span className={value === InstantShipOrderRecordType.SUCCESS ? "instant-ship-result-success" : ""}>
                {instantShipOrderRecordTypeNameMap[value] || value}
            </span>
        ),
    },
];

const TaskModal: React.FC<TaskModalProps> = observer(function (props) {
    const { open, onCancel, shop, instantShipStore, taskId } = props;
    const [task, setTask] = useState<InstantShipTask>(() => instantShipStore.currentTask);
    const [records, setRecords] = useState<InstantShipOrderRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageNo, setPageNo] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    const [totalCount, setTotalCount] = useState(0);
    const [recordFilter, setRecordFilter] = useState<InstantShipRecordFilter>(ALL_RECORD_FILTER);
    const refreshingRef = useRef(false);
    const recordsRequestIdRef = useRef(0);
    const recordFilterRef = useRef<InstantShipRecordFilter>(ALL_RECORD_FILTER);

    const loadRecords = useCallback(
        async (
            nextPageNo = pageNo,
            nextPageSize = pageSize,
            hideModal = true,
            nextFilter = recordFilterRef.current,
        ) => {
            const requestId = ++recordsRequestIdRef.current;
            setLoading(true);

            try {
                const result = await getInstantShipOrderRecords(
                    {
                        shopId: shop.id,
                        taskId,
                        type: nextFilter === ALL_RECORD_FILTER ? undefined : nextFilter,
                        pageNo: nextPageNo,
                        pageSize: nextPageSize,
                    },
                    { hideModal },
                );

                if (requestId !== recordsRequestIdRef.current) return;

                setRecords(result?.items || []);
                setTotalCount(result?.totalCount || 0);
                setPageNo(result?.pageNo || nextPageNo);
                setPageSize(result?.pageSize || nextPageSize);
            } catch (error) {
                if (!hideModal) throw error;
            } finally {
                if (requestId === recordsRequestIdRef.current) {
                    setLoading(false);
                }
            }
        },
        [shop.id, taskId, pageNo, pageSize],
    );

    useEffect(() => {
        loadRecords(1, PAGE_SIZE, false).catch(() => undefined);
    }, [taskId]);

    useEffect(() => {
        const timer = window.setInterval(async () => {
            if (refreshingRef.current) return;
            refreshingRef.current = true;

            try {
                const currentTask = await instantShipStore.loadCurrentTask(shop.id);
                if (currentTask?.id === taskId) {
                    setTask(currentTask);
                } else if (!currentTask) {
                    setTask((previousTask) =>
                        previousTask ? { ...previousTask, status: InstantShipTaskStatus.CLOSED } : previousTask,
                    );
                }
                await loadRecords(pageNo, pageSize);
            } finally {
                refreshingRef.current = false;
            }
        }, 10 * 1000);

        return () => window.clearInterval(timer);
    }, [shop.id, taskId, pageNo, pageSize, loadRecords]);

    function handleTableChange(pagination: TablePaginationConfig) {
        const nextPageNo = pagination.current || 1;
        const nextPageSize = pagination.pageSize || PAGE_SIZE;
        loadRecords(nextPageNo, nextPageSize, false).catch(() => undefined);
    }

    function handleRecordFilterChange(filter: InstantShipRecordFilter) {
        const nextFilter = recordFilter === filter ? ALL_RECORD_FILTER : filter;

        recordFilterRef.current = nextFilter;
        setRecordFilter(nextFilter);
        setPageNo(1);
        loadRecords(1, pageSize, false, nextFilter).catch(() => undefined);
    }

    async function handleCloseTask() {
        await confirm("确认关闭极速发货吗？");
        await instantShipStore.closeTask(shop.id);
        onCancel();
    }

    const activeTask = task || instantShipStore.currentTask;
    const printConfig = activeTask?.printConfig;
    const running = activeTask?.status === InstantShipTaskStatus.RUNNING;

    const it = printConfig?.shippingAddress;
    const address = it
        ? [it.shipperName, "，", it.province, it.city, it.district, it.street, "，", it.mobile, " ", it.telephone].join(
              "",
          )
        : "";

    return (
        <Modal
            width={1450}
            className="instant-ship-task-modal"
            title={
                <div className="instant-ship-task-title">
                    <Badge status={running ? "processing" : "default"} color={running ? "#52c41a" : undefined} />
                    <span>{running ? "极速发货中" : "极速发货已关闭"}</span>
                </div>
            }
            open={open}
            onCancel={onCancel}
            footer={
                running ? (
                    <AutoLoadingButton type="primary" onClick={handleCloseTask}>
                        关闭极速发货
                    </AutoLoadingButton>
                ) : null
            }
        >
            <div className="instant-ship-task-content">
                <Alert
                    className="instant-ship-task-tip"
                    type="error"
                    title={
                        <div>
                            默认过滤非待发货、已打印、挂起、风控、退款中、代收货款、部分发货、分销代发、异常、锁定、黑名单订单；订单同步可能有1~60秒延迟请耐心等待；请及时关注极速发货订单并打印快递单发包裹
                            <a
                                target="_blank"
                                href="https://qw15p3s8hv.feishu.cn/wiki/NNQDwhTGAi5virkI6rscOFrQnTf"
                                className="ml5"
                            >
                                查看极速发货教程
                            </a>
                        </div>
                    }
                />

                <div className="instant-ship-task-info">
                    <div className="instant-ship-task-config">
                        <div>
                            <span className="label">当前店铺：</span>
                            <span>{shop.name}</span>
                        </div>
                        <div>
                            <span className="label">发货规则：</span>
                            <span>{instantShipPostPayIntervalNameMap[activeTask?.postPayInterval] || "-"}</span>
                        </div>
                        <div>
                            <span className="label">快递单模板：</span>
                            <span>{printConfig?.waybillTemplate?.name || "-"}</span>
                        </div>
                        <div>
                            <span className="label">发货人：</span>
                            <span>{address || "-"}</span>
                        </div>
                    </div>

                    <div className="instant-ship-task-statistics">
                        <span className="statistics-title">处理结果：</span>
                        <button
                            type="button"
                            className="statistics-item success"
                            data-active={recordFilter === InstantShipOrderRecordType.SUCCESS}
                            aria-pressed={recordFilter === InstantShipOrderRecordType.SUCCESS}
                            onClick={() => handleRecordFilterChange(InstantShipOrderRecordType.SUCCESS)}
                        >
                            <span>发货成功</span>
                            <strong>{activeTask?.successCount || 0}</strong>
                        </button>
                        <div className="statistics-failure-group">
                            <span className="statistics-failure-total">
                                <span>处理失败</span>
                                <strong>{activeTask?.failCount || 0}</strong>
                            </span>
                            <span className="statistics-failure-divider" />
                            <div className="statistics-failure-options" role="group" aria-label="处理失败筛选">
                                <button
                                    type="button"
                                    className="statistics-failure-option"
                                    data-active={recordFilter === InstantShipOrderRecordType.GENERATE_FAIL}
                                    aria-pressed={recordFilter === InstantShipOrderRecordType.GENERATE_FAIL}
                                    onClick={() => handleRecordFilterChange(InstantShipOrderRecordType.GENERATE_FAIL)}
                                >
                                    取号失败
                                </button>
                                <button
                                    type="button"
                                    className="statistics-failure-option"
                                    data-active={recordFilter === InstantShipOrderRecordType.SHIP_FAIL}
                                    aria-pressed={recordFilter === InstantShipOrderRecordType.SHIP_FAIL}
                                    onClick={() => handleRecordFilterChange(InstantShipOrderRecordType.SHIP_FAIL)}
                                >
                                    发货失败
                                </button>
                                <button
                                    type="button"
                                    className="statistics-failure-option"
                                    data-active={recordFilter === InstantShipOrderRecordType.SKIP}
                                    aria-pressed={recordFilter === InstantShipOrderRecordType.SKIP}
                                    onClick={() => handleRecordFilterChange(InstantShipOrderRecordType.SKIP)}
                                >
                                    跳过处理
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="instant-ship-record-title">
                    <span>实时记录</span>
                    <span className="text-gray text-small ml10">开始时间：{formatTime(activeTask?.startTime)}</span>
                    {running ? (
                        <span className="instant-ship-monitoring ml10 text-small">
                            正在持续监测新订单{" "}
                            <LoadingDots color="#52c41a" style={{ marginBottom: "4px" }} effect="scale" />
                        </span>
                    ) : null}
                </div>

                <Table<InstantShipOrderRecord>
                    rowKey="id"
                    size="small"
                    columns={columns}
                    dataSource={records}
                    loading={loading}
                    scroll={{ x: 1360, y: 460 }}
                    pagination={{
                        current: pageNo,
                        pageSize,
                        total: totalCount,
                        showSizeChanger: true,
                        showTotal: (total) => `共 ${total} 条`,
                    }}
                    onChange={handleTableChange}
                />
            </div>
        </Modal>
    );
});

export default TaskModal;
